import React, { useEffect, useRef, useState, useMemo } from "react";
import "../Style/Timeline.css";
import { useLanguage } from "../Context/Languagecontext";

// Structural design and animation layout schema metadata kept separate from content translations
const TIMELINE_LAYOUT_CONFIG = [
  { id: "m1", year: "1998", accent: "#E8C87A", index: "01" },
  { id: "m2", year: "2003", accent: "#C0392B", index: "02" },
  { id: "m3", year: "2008", accent: "#E8C87A", index: "03" },
  { id: "m4", year: "2013", accent: "#C0392B", index: "04" },
  { id: "m5", year: "2020", accent: "#E8C87A", index: "05" },
];

export default function MilestonesTimeline() {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  
  const { t, lang } = useLanguage();
  const timelineData = t.milestonesTimeline;

  /* ── Structural Assembly combining Translation Dictionaries with Base Mappings ── */
  const localizedMilestones = useMemo(() => {
    const rawMilestonesCopy = timelineData?.milestones || {};
    return TIMELINE_LAYOUT_CONFIG.map((item) => {
      const copy = rawMilestonesCopy[item.id] || {};
      return {
        ...item,
        title: copy.title || '',
        description: copy.description || '',
      };
    });
  }, [timelineData]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold: 0.1 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className={`tl-section ${lang === 'ta' ? 'tl-section--tamil' : ''}`} ref={sectionRef}>
      {/* Noise grain overlay */}
      <div className="tl-grain" />

      {/* Decorative large background watermark */}
      <div className="tl-bg-word" aria-hidden="true">
        {timelineData?.backgroundWord}
      </div>

      {/* Header */}
      <div className={`tl-header ${inView ? "tl-header--in" : ""}`}>
        <div className="tl-header__tag">{timelineData?.headerTag}</div>
        <h2 className="tl-header__title">
          {timelineData?.titleNormal}
          <span className="tl-header__title-amp"> {timelineData?.titleAmpersand} </span>
          {timelineData?.titleLegacy}
        </h2>
        <p className="tl-header__sub">{timelineData?.subtitle}</p>
      </div>

      {/* Desktop: staggered column layout */}
      <div className="tl-grid">
        {localizedMilestones.map((m, i) => (
          <div
            key={m.id}
            className={`tl-card ${inView ? "tl-card--in" : ""} ${activeIndex === i ? "tl-card--active" : ""}`}
            style={{
              "--delay": `${i * 0.25}s`,
              "--accent": m.accent,
              "--offset": `${i % 2 === 0 ? "0px" : "80px"}`,
            }}
            onMouseEnter={() => setActiveIndex(i)}
            onMouseLeave={() => setActiveIndex(null)}
          >
            {/* Number */}
            <div className="tl-card__num">{m.index}</div>

            {/* Year pill */}
            <div className="tl-card__year">{m.year}</div>

            {/* Content */}
            <div className="tl-card__body">
              <h3 className="tl-card__title">{m.title}</h3>
              <div className="tl-card__rule" aria-hidden="true" />
              <p className="tl-card__desc">{m.description}</p>
            </div>

            {/* Accent glow corner */}
            <div className="tl-card__glow" aria-hidden="true" />

            {/* Bottom bar */}
            <div className="tl-card__bar" aria-hidden="true" />
          </div>
        ))}

        {/* Vertical spine line */}
        <div className={`tl-spine ${inView ? "tl-spine--in" : ""}`}>
          <div className="tl-spine__line" aria-hidden="true" />
        </div>
      </div>

      {/* Mobile vertical layout */}
      <div className="tl-mobile">
        {localizedMilestones.map((m, i) => (
          <div
            key={m.id}
            className={`tl-mob-item ${inView ? "tl-mob-item--in" : ""}`}
            style={{ "--delay": `${0.1 + i * 0.13}s`, "--accent": m.accent }}
          >
            <div className="tl-mob-item__left">
              <div className="tl-mob-item__dot" />
              {i < localizedMilestones.length - 1 && (
                <div className="tl-mob-item__connector" aria-hidden="true" />
              )}
            </div>
            <div className="tl-mob-item__right">
              <div className="tl-mob-item__meta">
                <span className="tl-mob-item__num">{m.index}</span>
                <span className="tl-mob-item__year">{m.year}</span>
              </div>
              <h3 className="tl-mob-item__title">{m.title}</h3>
              <p className="tl-mob-item__desc">{m.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom signature */}
      <div className={`tl-footer ${inView ? "tl-footer--in" : ""}`}>
        <span className="tl-footer__line" aria-hidden="true" />
        <span className="tl-footer__text">{timelineData?.footerText}</span>
        <span className="tl-footer__line" aria-hidden="true" />
      </div>
    </section>
  );
}